import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

interface ProfileData {
  name?: string;
  nameKana?: string;
  zip?: string;
  address?: string;
  phone?: string;
  email?: string;
}

interface GuardianData {
  name?: string;
  nameKana?: string;
  relationship?: string;
  zip?: string;
  address?: string;
  phone?: string;
  email?: string;
}

interface EnrollmentDraft {
  id: string;
  status: string;
  currentStep: number;
  profileData: ProfileData;
  guardianData: GuardianData;
  photoUrl: string | null;
  pledgeAgreed: boolean;
}

// ... imports
interface Application {
  id: string;
  applicantId: string;
  status: string;
  [key: string]: unknown;
}

export const EnrollmentPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id');
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(!!applicationId);

  const [enrollment, setEnrollment] = useState<EnrollmentDraft | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Initial Fetch
  useEffect(() => {
    if (!applicationId) return;

    const fetchEnrollment = async () => {
      try {
        const apiBase = import.meta.env.VITE_API_BASE || '';
        const token = localStorage.getItem('accessToken');

        // Fetch App (Existing logic)
        const resApp = await fetch(`${apiBase}/api/applications/${applicationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resApp.ok) setApplication((await resApp.json()) as Application);

        // Fetch Enrollment Draft
        const resEnroll = await fetch(`${apiBase}/api/applications/${applicationId}/enrollment`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (resEnroll.ok) {
          const data = await resEnroll.json();
          setEnrollment(data);
          setCurrentStep(data.currentStep || 1);
        } else if (resEnroll.status === 404) {
          // Not started
          setEnrollment({
            id: crypto.randomUUID(),
            status: 'DRAFT',
            currentStep: 1,
            profileData: {},
            guardianData: {},
            photoUrl: null,
            pledgeAgreed: false,
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollment();
  }, [applicationId]);

  const handleSave = async (data: Partial<EnrollmentDraft>, nextStep?: number) => {
    if (!enrollment) return;
    setSaving(true);
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '';
      const token = localStorage.getItem('accessToken');

      const payload = {
        ...enrollment,
        ...data,
        currentStep: nextStep || currentStep,
      };

      await fetch(`${apiBase}/api/applications/${applicationId}/enrollment`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setEnrollment(payload);
      if (nextStep) setCurrentStep(nextStep);
    } catch (e) {
      console.error(e);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Final submit
    const confirm = window.confirm('Are you sure you want to finalize enrollment?');
    if (!confirm) return;

    try {
      const apiBase = import.meta.env.VITE_API_BASE || '';
      const token = localStorage.getItem('accessToken');
      await fetch(`${apiBase}/api/applications/${applicationId}/enrollment/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Enrollment submitted!');
      window.location.href = '/dashboard';
    } catch (_e) {
      alert('Error submitting');
    }
  };

  const handlePhotoUpload = async (file: File) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const apiBase = import.meta.env.VITE_API_BASE || 'https://admission-api.suido.net';
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiBase}/api/applications/${applicationId}/enrollment/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        const result = await res.json();
        if (enrollment) {
          setEnrollment({ ...enrollment, photoUrl: result.photoUrl });
        }
      } else {
        alert('Photo upload failed');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!applicationId) return <div className="p-8">Select application from dashboard.</div>;
  if (loading) return <div className="p-8">Loading...</div>;
  const handleZipLookup = (zip: string, type: 'profile' | 'guardian') => {
    if (zip.length === 7) {
      // Mock zip lookup
      const mockAddress = '東京都新宿区西新宿2-8-1';
      if (type === 'profile' && enrollment) {
        setEnrollment({
          ...enrollment,
          profileData: { ...enrollment.profileData, address: mockAddress },
        });
      } else if (type === 'guardian' && enrollment) {
        setEnrollment({
          ...enrollment,
          guardianData: { ...enrollment.guardianData, address: mockAddress },
        });
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Step 1: {t('enrollment.step1')} (Profile)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('enrollment.profile.name')} (Name)</Label>
                    <Input
                      value={enrollment?.profileData?.name || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          profileData: {
                            ...enrollment.profileData,
                            name: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.profile.kana')} (Kana)</Label>
                    <Input
                      value={enrollment?.profileData?.nameKana || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          profileData: {
                            ...enrollment.profileData,
                            nameKana: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.profile.zip')} (Zip Code)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="1234567"
                      value={enrollment?.profileData?.zip || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          profileData: {
                            ...enrollment.profileData,
                            zip: e.target.value,
                          },
                        })
                      }
                    />
                    <Button
                      variant="secondary"
                      onClick={() => handleZipLookup(enrollment?.profileData?.zip || '', 'profile')}
                    >
                      {t('enrollment.profile.inputAddress')}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.profile.address')} (Address)</Label>
                  <Input
                    value={enrollment?.profileData?.address || ''}
                    onChange={(e) =>
                      enrollment &&
                      setEnrollment({
                        ...enrollment,
                        profileData: {
                          ...enrollment.profileData,
                          address: e.target.value,
                        },
                      })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('enrollment.profile.phone')} (Phone)</Label>
                    <Input
                      value={enrollment?.profileData?.phone || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          profileData: {
                            ...enrollment.profileData,
                            phone: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.profile.email')} (Email)</Label>
                    <Input
                      value={enrollment?.profileData?.email || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          profileData: {
                            ...enrollment.profileData,
                            email: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Button onClick={() => handleSave({}, 2)} disabled={saving}>
                    {t('common.next')}: {t('enrollment.step2')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Step 2: {t('enrollment.step2')} (Guardian)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('enrollment.guardian.name')} (Name)</Label>
                    <Input
                      value={enrollment?.guardianData?.name || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          guardianData: {
                            ...enrollment.guardianData,
                            name: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('enrollment.guardian.relationship')} (Relationship)</Label>
                    <Input
                      placeholder={t('enrollment.guardian.relationshipPlaceholder')}
                      value={enrollment?.guardianData?.relationship || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          guardianData: {
                            ...enrollment.guardianData,
                            relationship: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.profile.zip')} (Zip Code)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="1234567"
                      value={enrollment?.guardianData?.zip || ''}
                      onChange={(e) =>
                        enrollment &&
                        setEnrollment({
                          ...enrollment,
                          guardianData: {
                            ...enrollment.guardianData,
                            zip: e.target.value,
                          },
                        })
                      }
                    />
                    <Button
                      variant="secondary"
                      onClick={() => handleZipLookup(enrollment?.guardianData?.zip || '', 'guardian')}
                    >
                      {t('enrollment.profile.inputAddress')}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.profile.address')} (Address)</Label>
                  <Input
                    value={enrollment?.guardianData?.address || ''}
                    onChange={(e) =>
                      enrollment &&
                      setEnrollment({
                        ...enrollment,
                        guardianData: {
                          ...enrollment.guardianData,
                          address: e.target.value,
                        },
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('enrollment.profile.phone')} (Phone)</Label>
                  <Input
                    value={enrollment?.guardianData?.phone || ''}
                    onChange={(e) =>
                      enrollment &&
                      setEnrollment({
                        ...enrollment,
                        guardianData: {
                          ...enrollment.guardianData,
                          phone: e.target.value,
                        },
                      })
                    }
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    {t('common.back')}
                  </Button>
                  <Button onClick={() => handleSave({}, 3)} disabled={saving}>
                    {t('enrollment.photo.next')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 3: {
        const apiBase = import.meta.env.VITE_API_BASE || 'https://admission-api.suido.net';
        const photoUrl = enrollment?.photoUrl ? `${apiBase}/api/applications/${applicationId}/enrollment/photo` : null;

        return (
          <Card>
            <CardHeader>
              <CardTitle>Step 3: Photo Upload</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-48 w-40 bg-gray-100 flex items-center justify-center border rounded overflow-hidden">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt="ID"
                      className="h-full w-full object-cover"
                      key={enrollment?.photoUrl} // Force reload on change
                    />
                  ) : (
                    <div className="text-center p-4 text-xs text-gray-400">Face photo for Student ID</div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="photo">Select Image File</Label>
                  <Input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                    }}
                  />
                  <p className="text-[10px] text-gray-400">Supported formats: JPG, PNG. Max size 5MB.</p>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    {t('common.back')}
                  </Button>
                  <Button onClick={() => handleSave({}, 4)} disabled={saving || !enrollment?.photoUrl}>
                    {t('enrollment.pledge.next')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      }
      case 4:
        return (
          <Card>
            <CardHeader>
              <CardTitle>Step 4: {t('enrollment.step3')} (Pledge)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border p-4 bg-gray-50 text-sm h-48 overflow-y-auto font-serif leading-relaxed">
                  <p className="font-bold mb-2">{t('enrollment.pledge.title')}</p>
                  <p>{t('enrollment.pledge.content')}</p>
                </div>
                <div className="flex items-center space-x-2 p-2 border rounded bg-blue-50">
                  <input
                    type="checkbox"
                    id="pledge"
                    className="h-4 w-4"
                    checked={enrollment?.pledgeAgreed || false}
                    onChange={(e) => {
                      if (enrollment) {
                        setEnrollment({
                          ...enrollment,
                          pledgeAgreed: e.target.checked,
                        });
                      }
                    }}
                  />
                  <Label htmlFor="pledge" className="cursor-pointer">
                    {t('enrollment.pledge.agree')}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentStep(3)}>
                    {t('common.back')}
                  </Button>
                  <Button onClick={() => handleSave({}, 5)} disabled={saving || !enrollment?.pledgeAgreed}>
                    {t('enrollment.pledge.next')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      case 5:
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('enrollment.payment.titleFull')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <section>
                  <h4 className="font-bold border-b pb-1 mb-2">{t('enrollment.payment.title')}</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      `${t('enrollment.payment.credit')} (Credit Card)`,
                      `${t('enrollment.payment.convenience')} (Convenience Store)`,
                      `${t('enrollment.payment.bank')} (Bank Transfer)`,
                    ].map((method, idx) => (
                      <div
                        key={method}
                        className="flex items-center gap-2 p-3 border rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          id={`payment-${idx}`}
                          name="payment"
                          defaultChecked={method.includes('Credit')}
                        />
                        <label htmlFor={`payment-${idx}`} className="text-sm cursor-pointer flex-1">
                          {method}
                        </label>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="bg-gray-50 p-4 rounded text-sm space-y-3">
                  <h4 className="font-bold text-gray-700">{t('enrollment.confirm.title')}</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <span className="text-gray-500">{t('enrollment.confirm.labels.name')}:</span>
                    <span>{enrollment?.profileData?.name}</span>
                    <span className="text-gray-500">{t('enrollment.confirm.labels.address')}:</span>
                    <span>{enrollment?.profileData?.address}</span>
                    <span className="text-gray-500">{t('enrollment.confirm.labels.phone')}:</span>
                    <span>{enrollment?.profileData?.phone}</span>

                    <div className="col-span-2 border-t my-2" />

                    <span className="text-gray-500">{t('enrollment.confirm.labels.guardian')}:</span>
                    <span>{enrollment?.guardianData?.name}</span>
                    <span className="text-gray-500">{t('enrollment.confirm.labels.contact')}:</span>
                    <span>{enrollment?.guardianData?.phone}</span>
                  </div>
                </section>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(4)}>
                    {t('enrollment.confirm.return')}
                  </Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleSubmit} disabled={saving}>
                    {t('enrollment.confirm.submitFull')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Enrollment Procedure</h1>
        <div className="text-sm text-gray-500">Ref: {application?.id?.slice(0, 8)}</div>
      </div>

      {/* Progress Bar */}
      <div className="flex mb-8 gap-1">
        {[1, 2, 3, 4, 5].map((step) => (
          <div key={step} className={`h-2 flex-1 rounded ${step <= currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} />
        ))}
      </div>

      {renderStep()}
    </div>
  );
};
